class ProductSlider {
    constructor(selector, options = {}) {
        this.container = typeof selector === 'string' ? find(selector) : selector;

        if (!this.container) {
            return;
        }

        // Default options
        this.options = {
            transitionDuration: 600,
            ...options
        };

        this.init();
    }

    init() {
        this.slidesContainer = this.container.find('.slides');
        this.originalSlides = this.slidesContainer.findAll('.slide');
        this.slideLabelsContainer = this.container.find('.slide-labels');
        this.slideLabels = this.slideLabelsContainer.findAll('.slide-label');
        this.paginationContainer = this.container.find('.pagination');
        this.paginationButtons = this.paginationContainer.findAll('.pagination-button');

        if (!this.slidesContainer || this.originalSlides.length === 0) {
            console.error('ProductSlider: No slides found');
            return;
        }

        // Store the original count
        this.originalSlideCount = this.originalSlides.length;

        // Special handling for single product
        if (this.originalSlideCount === 1) {
            // Just show the single product, no sliding needed
            this.container.classList.add('single-product');
            // Hide pagination if it exists
            if (this.paginationContainer) {
                this.paginationContainer.style.display = 'none';
            }
            return; // Exit early, no slider functionality needed
        }

        // Create array of slide elements from originalSlides object
        this.slides = Array.from(this.originalSlides);

        // Build the slide array based on product count
        if (this.originalSlideCount === 2) {
            // For 2 products: we need [1, 0, 1, 0] pattern
            // Clone both slides
            const clone0 = this.slides[0].cloneNode(true);
            const clone1 = this.slides[1].cloneNode(true);
            clone0.setAttribute('data-position', '0');
            clone1.setAttribute('data-position', '1');
            this.slides = [clone1, this.slides[0], this.slides[1], clone0];


        } else if (this.originalSlideCount === 3) {
            // For 3 products: Create enough slides for smooth infinite scroll
            // We'll create 2 full sets = 6 slides total, then rotate
            this.slides = this.slides.concat(this.slides.map(slide => {
                const clone = slide.cloneNode(true);
                clone.setAttribute('data-position', slide.getAttribute('data-position'));
                return clone;
            }));

            // Rotate the array right by one position
            let lastSlide = this.slides.pop();
            this.slides.unshift(lastSlide);

        } else if (this.originalSlideCount >= 4) {
            // For 4+ products: rotate right by one
            let lastSlide = this.slides.pop();
            this.slides.unshift(lastSlide);
        }

        this.maxScrollPosition = this.originalSlideCount; // Always use original count for scroll wrapping

        // Put new slides in container
        this.slidesContainer.innerHTML = '';
        this.slides.forEach((slide, position) => {
            slide.position = position;
            this.slidesContainer.appendChild(slide);
        });

        this.scrollPosition = 0;
        this.scrollSpeed = 0;
        this.continuousScrollPosition = 0; // Track continuous position without wrapping

        this.handleMoveEvents();
        this.handlePaginationClicks();

        this.updateStylesForPosition();
        this.updateActiveStates();
    }

    updateActiveStates() {
        // Find the slide at position 1 (the main visible slide)
        let visibleSlideIndex = 1 - Math.round(this.scrollPosition);

        // Normalize to positive index
        while (visibleSlideIndex < 0) {
            visibleSlideIndex += this.slides.length;
        }
        visibleSlideIndex = visibleSlideIndex % this.slides.length;

        const visibleSlide = this.slides[visibleSlideIndex];

        if (visibleSlide) {
            const activeDataPosition = visibleSlide.getAttribute('data-position');

            // Update pagination buttons
            if (this.paginationButtons) {
                this.paginationButtons.forEach(button => {
                    if (button.getAttribute('data-position') === activeDataPosition) {
                        button.classList.add('active');
                    } else {
                        button.classList.remove('active');
                    }
                });
            }

            // Update slide labels
            if (this.slideLabels) {
                this.slideLabels.forEach(label => {
                    if (label.getAttribute('data-position') === activeDataPosition) {
                        label.classList.add('active');
                    } else {
                        label.classList.remove('active');
                    }
                });
            }
        }
    }

    // give each slide the style for its position
    updateStylesForPosition() {
        this.slides.forEach((slide, slideIndex) => {
            let position;

            if (this.originalSlideCount === 3 && this.slides.length === 6) {
                // For 3 products with 6 slides (doubled and rotated)
                // Calculate position with proper wrapping
                position = (this.continuousScrollPosition + slideIndex) % 6;
                if (position < 0) position += 6;

                // Map the 6 positions to our 5 keyframe positions (0-4)
                // Positions 0,1,2,3,4 map directly
                // Position 5 wraps back to position 2 (the third visible slide repeats)
                if (position >= 5) {
                    position = position - 3;
                }
            } else {
                // For other configurations, use simple wrapping
                position = this.scrollPosition + slideIndex;
                const wrapAt = this.slides.length;
                position = ((position % wrapAt) + wrapAt) % wrapAt;
            }

            let style = this.getStyleForPosition(position);

            slide.style.transform = `translateX(${style.transformX}px) translateY(${style.transformY}px) scale(${style.scale}) rotate(${style.rotate}deg)`;
            slide.style.opacity = style.opacity;
            slide.style.zIndex = style.zIndex;
            if (style.blur > 0) {
                slide.style.filter = `blur(${style.blur}px)`;
            } else {
                slide.style.filter = 'none';
            }
            slide.style.pointerEvents = style.pointerEvents;
        });
    }

    stopAnimation() {
        if (this.inertiaAnimation) {
            cancelAnimationFrame(this.inertiaAnimation);
            this.inertiaAnimation = null;
        }
    }

    animateWithInertia() {
        // Stop any existing animation first
        this.stopAnimation();

        // Constants for physics behavior
        const friction = 0.95; // Lower = more friction
        const minSpeed = 0.25; // Threshold to start snapping
        const snapDistance = 0.01; // Distance threshold to complete snap

        let lastTime = Date.now();
        let snapping = false;
        let targetPosition = null;

        const animate = () => {
            const currentTime = Date.now();
            const delta = (currentTime - lastTime) / 16; // Normalize to ~60fps
            lastTime = currentTime;

            // Determine target position based on direction of movement
            if (!snapping && Math.abs(this.scrollSpeed) < minSpeed) {
                snapping = true;

                // Snap in the direction of movement (or nearest if speed is very low)
                if (Math.abs(this.scrollSpeed) < 0.001) {
                    // Very low speed - snap to nearest
                    targetPosition = Math.round(this.scrollPosition);
                } else if (this.scrollSpeed > 0) {
                    // Moving forward - snap to next integer
                    targetPosition = Math.ceil(this.scrollPosition);
                } else {
                    // Moving backward - snap to previous integer
                    targetPosition = Math.floor(this.scrollPosition);
                }
            }

            if (snapping && targetPosition !== null) {
                // Calculate distance to target
                let distance = targetPosition - this.scrollPosition;

                // If we're very close, just snap directly
                if (Math.abs(distance) < snapDistance) {
                    this.scrollPosition = targetPosition;
                    if (this.originalSlideCount === 3) {
                        this.continuousScrollPosition = targetPosition;
                    }
                    this.scrollSpeed = 0;

                    // Normalize scrollPosition after snapping
                    this.scrollPosition = ((this.scrollPosition % this.maxScrollPosition) + this.maxScrollPosition) % this.maxScrollPosition;

                    this.updateStylesForPosition();
                    this.updateActiveStates();
                    this.inertiaAnimation = null;
                    return;
                }

                // Use easing function for smooth approach
                const progress = 1 - Math.pow(0.92, delta);
                const movement = distance * progress;
                this.scrollPosition += movement;
                if (this.originalSlideCount === 3) {
                    this.continuousScrollPosition += movement;
                }

                // Update speed to reflect movement
                this.scrollSpeed = distance * 0.05;
            } else {
                // Normal friction when not snapping
                this.scrollSpeed *= Math.pow(friction, delta);

                // Update scroll position based on current speed
                const movement = this.scrollSpeed * delta * 0.1;
                this.scrollPosition += movement;
                if (this.originalSlideCount === 3) {
                    this.continuousScrollPosition += movement;
                }
            }

            // Normalize scrollPosition continuously to prevent accumulation errors
            this.scrollPosition = ((this.scrollPosition % this.maxScrollPosition) + this.maxScrollPosition) % this.maxScrollPosition;

            // Update the UI
            this.updateStylesForPosition();
            this.updateActiveStates();

            // Stop animation if speed is essentially zero and we're not snapping
            if (!snapping && Math.abs(this.scrollSpeed) < 0.0001) {
                this.inertiaAnimation = null;
                return;
            }

            // Continue animation
            this.inertiaAnimation = requestAnimationFrame(animate);
        };

        this.inertiaAnimation = requestAnimationFrame(animate);
    }

    handleMoveEvents() {
        let startX = 0;
        let currentX = 0;
        let lastX = 0;
        let isDragging = false;
        let startTime = 0;
        let startScrollPosition = 0;
        let progress;

        const handleStart = (x) => {
            isDragging = true;
            this.stopAnimation();
            startX = x;
            currentX = x;
            startTime = Date.now();
            startScrollPosition = this.originalSlideCount === 3 ? this.continuousScrollPosition : this.scrollPosition;
            this.slidesContainer.style.cursor = 'grabbing';
        };

        const handleMove = (x) => {
            if (!isDragging) return;

            const currentTime = Date.now();
            lastX = currentX;
            const lastTime = this.lastMoveTime || currentTime;

            currentX = x;
            this.lastMoveTime = currentTime;

            const deltaX = currentX - startX;
            const timeDelta = Math.max(1, currentTime - lastTime);

            // Calculate instantaneous speed
            const instantSpeed = (currentX - lastX) / timeDelta;

            // Update moving average speed
            this.scrollSpeed = this.scrollSpeed * 0.3 + instantSpeed * 0.7;

            progress = deltaX / 300;

            // Update continuous position for 3 products
            if (this.originalSlideCount === 3) {
                this.continuousScrollPosition = startScrollPosition + progress;
            }

            // Update regular wrapped position
            this.scrollPosition = startScrollPosition + progress;

            // Wrap scrollPosition
            if (this.scrollPosition < 0) {
                const rotations = Math.ceil(Math.abs(this.scrollPosition) / this.maxScrollPosition);
                this.scrollPosition = this.maxScrollPosition * rotations + this.scrollPosition;
            } else {
                this.scrollPosition = this.scrollPosition % this.maxScrollPosition;
            }

            this.updateStylesForPosition();
        };

        const handleEnd = () => {
            if (!isDragging) return;

            isDragging = false;
            this.slidesContainer.style.cursor = '';

            // Calculate final speed considering time since last movement
            const timeSinceLastMove = Date.now() - this.lastMoveTime;

            // If the user hasn't moved in a while (e.g., 100ms), consider speed as 0
            if (timeSinceLastMove > 100) {
                this.scrollSpeed = 0;
            }

            // Always start animation to handle snapping, even at zero speed
            this.animateWithInertia();
        };

        // Mouse events
        this.slidesContainer.on('mousedown', (e) => {
            e.preventDefault();
            handleStart(e.clientX);
        });

        document.on('mousemove', (e) => {
            if (isDragging) {
                e.preventDefault();
                handleMove(e.clientX);
            }
        });

        document.on('mouseup', () => {
            handleEnd();
        });

        // Touch events
        this.slidesContainer.on('touchstart', (e) => {
            handleStart(e.touches[0].clientX);
        });

        this.slidesContainer.on('touchmove', (e) => {
            e.preventDefault();
            handleMove(e.touches[0].clientX);
        });

        this.slidesContainer.on('touchend', () => {
            handleEnd();
        });
    }

    handlePaginationClicks() {
        if (!this.paginationButtons) return;

        this.paginationButtons.forEach(button => {
            button.on('click', () => {
                const targetDataPosition = button.getAttribute('data-position');
                const currentActiveButton = this.paginationContainer.find('.pagination-button.active');
                const currentDataPosition = currentActiveButton ? currentActiveButton.getAttribute('data-position') : null;

                if (targetDataPosition === currentDataPosition) return; // Already on this slide

                // Find all instances of the target slide
                let targetScrollPosition = null;
                let minDistance = Infinity;

                this.slides.forEach((slide, index) => {
                    if (slide.getAttribute('data-position') === targetDataPosition) {
                        let possibleScrollPosition = 1 - index;

                        // Normalize to [0, maxScrollPosition)
                        while (possibleScrollPosition < 0) {
                            possibleScrollPosition += this.maxScrollPosition;
                        }
                        possibleScrollPosition = possibleScrollPosition % this.maxScrollPosition;

                        // Calculate shortest distance considering wrap-around
                        let distance = possibleScrollPosition - this.scrollPosition;

                        // Adjust for wrap-around to find shortest path
                        if (distance > this.maxScrollPosition / 2) {
                            distance -= this.maxScrollPosition;
                        } else if (distance < -this.maxScrollPosition / 2) {
                            distance += this.maxScrollPosition;
                        }

                        if (Math.abs(distance) < minDistance) {
                            minDistance = Math.abs(distance);
                            targetScrollPosition = possibleScrollPosition;
                        }
                    }
                });

                if (targetScrollPosition !== null) {
                    this.animateToPosition(targetScrollPosition);
                }
            });
        });
    }

    animateToPosition(targetPosition) {
        this.stopAnimation();

        // Normalize target position
        while (targetPosition < 0) {
            targetPosition += this.maxScrollPosition;
        }
        targetPosition = targetPosition % this.maxScrollPosition;

        const startPosition = this.scrollPosition;
        let distance = targetPosition - startPosition;

        // Choose shortest path considering wrap-around
        if (Math.abs(distance) > this.maxScrollPosition / 2) {
            if (distance > 0) {
                distance = distance - this.maxScrollPosition;
            } else {
                distance = distance + this.maxScrollPosition;
            }
        }

        const animationDuration = 400;
        const startTime = Date.now();

        const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(1, elapsed / animationDuration);

            // Ease out cubic
            const eased = 1 - Math.pow(1 - progress, 3);

            this.scrollPosition = startPosition + distance * eased;

            // Wrap around
            if (this.scrollPosition < 0) {
                this.scrollPosition = this.maxScrollPosition + this.scrollPosition;
            } else if (this.scrollPosition >= this.maxScrollPosition) {
                this.scrollPosition = this.scrollPosition % this.maxScrollPosition;
            }

            this.updateStylesForPosition();
            this.updateActiveStates();

            if (progress < 1) {
                this.inertiaAnimation = requestAnimationFrame(animate);
            } else {
                this.scrollPosition = targetPosition;
                this.updateStylesForPosition();
                this.updateActiveStates();
                this.inertiaAnimation = null;
            }
        };

        this.inertiaAnimation = requestAnimationFrame(animate);
    }

    // given scroll position, we get an interpolated style that fits
    getStyleForPosition(scrollPosition) {
        // for all slides that are out of our window
        let invisibleStyle = {
            transformX: 0,
            transformY: 0,
            scale: 0.2,
            rotate: 0,
            opacity: 0,
            blur: 0,
            zIndex: 0,
            pointerEvents: 'none'
        };

        let keyframes = [
            // position 0: invisible, big, left (entry from left)
            {
                transformX: -198,
                transformY: 90,
                scale: 1.504,
                rotate: 0,
                opacity: 0,
                blur: 0, // 5
                zIndex: 10,
                pointerEvents: 0
            },

            // position 1: main product
            {
                transformX: 0,
                transformY: 0,
                scale: 1,
                rotate: 0,
                opacity: 1,
                blur: 0,
                zIndex: 5,
                pointerEvents: 1
            }
        ];

        // Add position 2 if we have 2 or more products
        if (this.originalSlideCount >= 2) {
            keyframes.push({
                transformX: 95,
                transformY: -41,
                scale: 0.7717,
                rotate: 0,
                opacity: 1,
                blur: 0, // 2
                zIndex: 4,
                pointerEvents: 0
            });
        }

        // Add position 3 if we have 3 or more products
        if (this.originalSlideCount >= 3) {
            keyframes.push({
                transformX: 167,
                transformY: -72,
                scale: 0.6,
                rotate: 0,
                opacity: 1,
                blur: 0, // 5
                zIndex: 3,
                pointerEvents: 0
            });
        }

        // Add exit position (invisible, right, small)
        // This is position 3 for 2 products, position 4 for 3+ products
        keyframes.push({
            transformX: 223,
            transformY: -96,
            scale: 0.467,
            rotate: 0,
            opacity: 0,
            blur: 0, // 10
            zIndex: 2,
            pointerEvents: 0
        });

        // For positions beyond our keyframes, return invisible
        if (scrollPosition >= keyframes.length) {
            return invisibleStyle;
        }

        let position = Math.floor(scrollPosition);
        let nextPosition = position + 1;

        if (position < 0 || nextPosition >= keyframes.length) {
            return invisibleStyle;
        }

        let progress = scrollPosition - position; // fractional part

        // interpolate between keyframes
        return {
            transformX: keyframes[position].transformX + (keyframes[nextPosition].transformX - keyframes[position].transformX) * progress,
            transformY: keyframes[position].transformY + (keyframes[nextPosition].transformY - keyframes[position].transformY) * progress,
            scale: keyframes[position].scale + (keyframes[nextPosition].scale - keyframes[position].scale) * progress,
            rotate: keyframes[position].rotate + (keyframes[nextPosition].rotate - keyframes[position].rotate) * progress,
            opacity: keyframes[position].opacity + (keyframes[nextPosition].opacity - keyframes[position].opacity) * progress,
            blur: keyframes[position].blur + (keyframes[nextPosition].blur - keyframes[position].blur) * progress,
            zIndex: parseInt(keyframes[position].zIndex + (keyframes[nextPosition].zIndex - keyframes[position].zIndex) * progress),
            pointerEvents: parseInt(keyframes[position].pointerEvents + (keyframes[nextPosition].pointerEvents - keyframes[position].pointerEvents) * progress) === 0 ? 'none' : 'auto',
        };
    }
}

window.ProductSlider = ProductSlider;